import React, {ReactNode} from 'react'
import {Link, useLocation} from "react-router-dom"
import {Chip, Stack, SvgIconProps, SxProps, Tooltip} from "@mui/material"
import {SvgIconComponent} from "@mui/icons-material"

interface NavSidebarLinkProps {
    to: string
    baseUrl: string
    icon: SvgIconComponent
    chipLabel?: string
    collapsed?: boolean
    children: ReactNode
}

function NavSidebarLink({to, baseUrl, icon: Icon, chipLabel, collapsed, children}: NavSidebarLinkProps) {
    const base = baseUrl.replace(/\/$/, "")
    const url = to === "" ? (base || "/") : `${base}/${to}`

    const location = useLocation()
    const pathname = location.pathname.replace(/\/$/, "") || "/"

    const isActive = to === ""
        ? pathname === (base || "/")
        : pathname === url || pathname.startsWith(url + "/")

    const iconProps: SvgIconProps = {fontSize: "small", color: isActive ? "inherit" : "secondary"}
    const chipProps: { size: "small", sx: SxProps } = {
        size: "small",
        sx: {display: {md: "inherit", xs: "none"}},
    }

    const linkContent = (
        <Link
            to={url}
            style={{textDecoration: "none"}}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={collapsed ? "center" : "flex-start"}
                sx={{
                    px: collapsed ? 0 : 1.5,
                    py: 1,
                    borderRadius: '6px',
                    color: isActive ? '#4c49c1' : '#444',
                    bgcolor: isActive ? '#f3f2ff' : 'transparent',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.95rem',
                    minWidth: collapsed ? 40 : undefined,
                    minHeight: collapsed ? 40 : undefined,
                    '&:hover': {
                        bgcolor: isActive ? '#f3f2ff' : 'rgba(0,0,0,0.03)',
                    },
                }}
            >
                <Icon {...iconProps} />
                {!collapsed && <span style={{marginLeft: 8}}>{children}</span>}
            </Stack>
        </Link>
    )

    if (collapsed) {
        return (
            <Tooltip title={children} placement="right">
                {linkContent}
            </Tooltip>
        )
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {linkContent}
            {chipLabel && <Chip label={chipLabel} {...chipProps}/>}
        </Stack>
    )
}

export default NavSidebarLink
